import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { BudgetFilters } from '@/composables/useBudgetFilters';
import type { AgreementFilters } from '@/composables/useAgreementFilters';
import type { AgreementAthleteFilters } from '@/composables/useAgreementAthleteFilters';
import type { PaymentFilters } from '@/composables/usePaymentFilters';
import type { AthleteFilters } from '@/composables/useAthleteFilters';
import type { AgreementsByContractFilters } from '@/composables/useAgreementsByContractFilters';
import type { AgentFilters } from '@/composables/useAgentFilters';
import type { AgentDirectoryFilters } from '@/composables/useAgentDirectoryFilters';

/**
 * Helper to create filter storage with explicit default keys tracking.
 * Reduces boilerplate for the common pattern of storing filters + tracking which defaults were explicit.
 */
function createFilterStorage<T>() {
  const filters = ref<T | null>(null);
  const explicitDefaultKeys = ref<string[]>([]);

  return {
    filters,
    explicitDefaultKeys,
    setFilters: (value: T | null) => { filters.value = value; },
    setExplicitDefaultKeys: (keys: string[]) => { explicitDefaultKeys.value = keys; },
  };
}

export const useFiltersStore = defineStore('filters', () => {
  // Standard filter storage for each table type
  const budget = createFilterStorage<BudgetFilters>();
  const agreement = createFilterStorage<AgreementFilters>();
  const athlete = createFilterStorage<AthleteFilters>();
  const allPayment = createFilterStorage<PaymentFilters>();
  const agreementPayment = createFilterStorage<PaymentFilters>();
  const agreementsByContract = createFilterStorage<AgreementsByContractFilters>();
  const agent = createFilterStorage<AgentFilters>();
  const agentDirectory = createFilterStorage<AgentDirectoryFilters>();

  // Agreement athlete filters are keyed by agreement TWRN (for multi-tab support)
  const agreementAthleteFilters = ref<Record<string, AgreementAthleteFilters>>({});

  const setAgreementAthleteFilters = (agreementTwrn: string, filters: AgreementAthleteFilters) => {
    agreementAthleteFilters.value[agreementTwrn] = filters;
  };

  const getAgreementAthleteFilters = (agreementTwrn: string): AgreementAthleteFilters | null => {
    return agreementAthleteFilters.value[agreementTwrn] ?? null;
  };

  return {
    // Budget
    budgetFilters: budget.filters,
    setBudgetFilters: budget.setFilters,
    budgetExplicitDefaultKeys: budget.explicitDefaultKeys,
    setBudgetExplicitDefaultKeys: budget.setExplicitDefaultKeys,

    // Agreement
    agreementFilters: agreement.filters,
    setAgreementFilters: agreement.setFilters,
    agreementExplicitDefaultKeys: agreement.explicitDefaultKeys,
    setAgreementExplicitDefaultKeys: agreement.setExplicitDefaultKeys,

    // Agreement Athletes (keyed by agreement TWRN)
    agreementAthleteFilters,
    setAgreementAthleteFilters,
    getAgreementAthleteFilters,

    // Agreements By Contract
    agreementsByContractFilters: agreementsByContract.filters,
    setAgreementsByContractFilters: agreementsByContract.setFilters,

    // All Payments
    allPaymentFilters: allPayment.filters,
    setAllPaymentFilters: allPayment.setFilters,
    allPaymentExplicitDefaultKeys: allPayment.explicitDefaultKeys,
    setAllPaymentExplicitDefaultKeys: allPayment.setExplicitDefaultKeys,

    // Agreement Payments
    agreementPaymentFilters: agreementPayment.filters,
    setAgreementPaymentFilters: agreementPayment.setFilters,
    agreementPaymentExplicitDefaultKeys: agreementPayment.explicitDefaultKeys,
    setAgreementPaymentExplicitDefaultKeys: agreementPayment.setExplicitDefaultKeys,

    // Athlete
    athleteFilters: athlete.filters,
    setAthleteFilters: athlete.setFilters,
    athleteExplicitDefaultKeys: athlete.explicitDefaultKeys,
    setAthleteExplicitDefaultKeys: athlete.setExplicitDefaultKeys,

    // Agent
    agentFilters: agent.filters,
    setAgentFilters: agent.setFilters,

    // Agent Directory
    agentDirectoryFilters: agentDirectory.filters,
    setAgentDirectoryFilters: agentDirectory.setFilters,
  };
});
